import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { History, Filter, Building2, CalendarDays, Users, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status, isDarkMode }) => {
  const isActive = status === 'Ativa';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
      isActive
        ? 'bg-demoraes-green/10 text-demoraes-green'
        : isDarkMode ? 'bg-zinc-800 text-zinc-500' : 'bg-zinc-100 text-zinc-500'
    }`} data-testid={`status-badge-${isActive ? 'active' : 'closed'}`}>
      {isActive ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
      {status}
    </span>
  );
};

const ProgressBar = ({ percentage, isDarkMode }) => (
  <div className="flex items-center gap-2">
    <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-100'}`}>
      <div
        className={`h-full rounded-full transition-all ${percentage >= 100 ? 'bg-demoraes-green' : 'bg-amber-500'}`}
        style={{ width: `${Math.min(percentage, 100)}%` }}
      />
    </div>
    <span className={`text-xs font-medium tabular-nums w-10 text-right ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
      {percentage}%
    </span>
  </div>
);

export const RoundHistoryTab = ({ token, isDarkMode }) => {
  const [rounds, setRounds] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [filterTag, setFilterTag] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async (tag = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tag) params.append('tag', tag);
      const response = await axios.get(`${API}/admin/round-history?${params}`);
      if (response.data.success) {
        setRounds(response.data.rounds || []);
        if (!tag) setAvailableTags(response.data.available_tags || []);
      }
    } catch (error) {
      console.error('Error fetching round history:', error);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleFilterChange = (tag) => {
    setFilterTag(tag);
    fetchHistory(tag);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  return (
    <div className="space-y-4">
      {/* Header + Filter */}
      <div className={`rounded-lg border p-5 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <History size={20} className="text-demoraes-green" />
            <h3 className={`text-base font-bold ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}
              style={{ fontFamily: 'Manrope, sans-serif' }}>
              Histórico de Rodadas
            </h3>
            <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-zinc-100 text-zinc-600'}`}>
              {rounds.length} registros
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className={isDarkMode ? 'text-zinc-500' : 'text-zinc-400'} />
            <select
              value={filterTag}
              onChange={(e) => handleFilterChange(e.target.value)}
              className={`text-sm border rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-demoraes-green/30 ${
                isDarkMode ? 'bg-zinc-800 border-zinc-700 text-zinc-200' : 'bg-white border-zinc-300 text-zinc-700'
              }`}
              data-testid="round-history-filter"
            >
              <option value="">Todas as empresas</option>
              {availableTags.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-demoraes-green" />
          </div>
        ) : rounds.length === 0 ? (
          <div className="text-center py-16">
            <History size={40} className={`mx-auto mb-3 ${isDarkMode ? 'text-zinc-700' : 'text-zinc-300'}`} />
            <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>Nenhuma rodada encontrada.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${isDarkMode ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-100 bg-zinc-50'}`}>
                  <th className={`text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>Empresa</th>
                  <th className={`text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>Período</th>
                  <th className={`text-center px-4 py-3 font-semibold text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>Status</th>
                  <th className={`text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>Respostas</th>
                  <th className={`text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>Progresso</th>
                  <th className={`text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>Criado em</th>
                </tr>
              </thead>
              <tbody>
                {rounds.map((round) => (
                  <tr key={`${round.tag}-${round.year}-${round.round}-${round.created_at}`}
                    className={`border-b transition-colors ${
                      isDarkMode ? 'border-zinc-800 hover:bg-zinc-800/50' : 'border-zinc-50 hover:bg-zinc-50'
                    }`}
                    data-testid={`round-row-${round.tag}-${round.round}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 size={14} className={isDarkMode ? 'text-zinc-600' : 'text-zinc-400'} />
                        <div>
                          <div className={`font-medium ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>{round.client_name}</div>
                          <div className={`text-xs ${isDarkMode ? 'text-zinc-600' : 'text-zinc-400'}`}>{round.tag}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <CalendarDays size={13} className={isDarkMode ? 'text-zinc-600' : 'text-zinc-400'} />
                        <span className={`font-medium ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>{round.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={round.status} isDarkMode={isDarkMode} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Users size={13} className={isDarkMode ? 'text-zinc-600' : 'text-zinc-400'} />
                        <span className={`font-medium tabular-nums ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>
                          {round.response_count}
                        </span>
                        <span className={`text-xs ${isDarkMode ? 'text-zinc-600' : 'text-zinc-400'}`}>
                          / {round.employee_count}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 w-36">
                      <ProgressBar percentage={round.percentage} isDarkMode={isDarkMode} />
                    </td>
                    <td className={`px-4 py-3 text-xs tabular-nums ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
                      {formatDate(round.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
